import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type { SubmissionFilter, SubmissionSort } from "@sga/shared";
import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api.js";

/**
 * Server state for the submissions table — docs/01-PRODUCT-SPEC.md §9.1.
 *
 * All of it goes through TanStack Query (CLAUDE.md: "Server state through TanStack Query, never
 * `useEffect` fetching").
 *
 * Paging is by keyset cursor, and the screen shows one page at a time rather than an endless
 * scroll. Infinite scroll answers "show me more" well and "is this person in here" badly: there was
 * no page number, no total, and no way to be sure you had reached the end short of scrolling to it.
 *
 * Keyset rather than OFFSET even though the UI now looks like classic paging: on deadline day rows
 * arrive while a coordinator reads, and OFFSET 40 silently shifts the window under them so a row
 * they have not seen slides off the page they are on. A cursor names a position, not a distance.
 * The cost is that a cursor can only step forward, so previous pages are remembered as a stack
 * rather than computed — which is why there is no "jump to page 7".
 */

export interface SubmissionRow {
  id: string;
  reference: string | null;
  randomId: string | null;
  status: string;
  reviewStatus: string;
  submittedAt: string | null;
  lastSavedAt: string;
  source: string;
  duplicateFlag: boolean;
  isTest: boolean;
  respondentName: string | null;
  respondentEmail: string | null;
  data: Record<string, unknown>;
}

export interface SubmissionPage {
  data: SubmissionRow[];
  page: { limit: number; hasMore: boolean; total: number; nextCursor: string | null };
  view: { columns: string[]; filters: SubmissionFilter[]; sort: SubmissionSort };
  piiMasked: boolean;
}

export interface ColumnDefinition {
  key: string;
  label: string;
  type: string;
  /** How the server says this column orders — see `sortKindForFieldType` in `@sga/shared`. */
  sortKind: "numeric" | "temporal" | "text";
  isPii: boolean;
  /** Encrypted at rest. Never has a value in a row — the export dialog is the only thing that acts
   * on it, and only for a superadmin who states a reason. */
  isSensitive: boolean;
  masked: boolean;
}

export interface SavedView {
  id: string;
  name: string;
  columns: string[];
  filters: SubmissionFilter[];
  sort: SubmissionSort;
  isShared: boolean;
  isDefault: boolean;
  isMine: boolean;
  ownerName: string;
}

export interface SubmissionQueryState {
  filters: SubmissionFilter[];
  sort: SubmissionSort;
  q?: string;
  viewId?: string;
  includeTest: boolean;
}

const toParams = (state: SubmissionQueryState, pageSize: number, cursor?: string): string => {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (state.q) params.set("q", state.q);
  if (state.viewId) params.set("viewId", state.viewId);
  if (state.includeTest) params.set("includeTest", "true");
  if (state.filters.length > 0) params.set("filters", JSON.stringify(state.filters));
  params.set("sort", JSON.stringify(state.sort));
  params.set("limit", String(pageSize));
  return params.toString();
};

/** The sizes offered in the picker. 200 is the API's own ceiling (`submissionListQuerySchema`). */
export const PAGE_SIZES = [20, 50, 100, 200] as const;
export const DEFAULT_PAGE_SIZE = 50;

export interface SubmissionsPaging {
  pageSize: number;
  setPageSize: (size: number) => void;
  /** 1-based, for display. */
  pageNumber: number;
  /** The 1-based index of the first row on this page, and of the last. */
  firstRow: number;
  lastRow: number;
  total: number;
  hasPrevious: boolean;
  hasNext: boolean;
  goNext: () => void;
  goPrevious: () => void;
}

export const useSubmissions = (formId: string, state: SubmissionQueryState) => {
  const [pageSize, setPageSizeRaw] = useState<number>(DEFAULT_PAGE_SIZE);
  // The cursor for each page visited, so Previous can step back. index 0 is the first page, which
  // has no cursor; a cursor can only be followed forward, so the way back is to remember.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [index, setIndex] = useState(0);

  // A filter, a sort or a search that changes makes every remembered cursor meaningless — it names
  // a position in a result set that no longer exists. Serialised rather than compared by reference
  // because `state` is rebuilt on every render of the page above.
  const stateKey = JSON.stringify(state);
  const [lastStateKey, setLastStateKey] = useState(stateKey);
  if (lastStateKey !== stateKey) {
    setLastStateKey(stateKey);
    setCursors([undefined]);
    setIndex(0);
  }

  const query = useQuery({
    queryKey: ["submissions", formId, stateKey, pageSize, index],
    queryFn: () =>
      apiGet<SubmissionPage>(
        `/forms/${formId}/submissions?${toParams(state, pageSize, cursors[index])}`,
      ),
    // Paging back and forth should not blank the table each time.
    placeholderData: (previous) => previous,
  });

  const setPageSize = useCallback((size: number) => {
    setPageSizeRaw(size);
    // Row 41 of 20-per-page is not row 41 of 100-per-page, and the remembered cursors were minted
    // for the old size. Start again rather than land somewhere arbitrary.
    setCursors([undefined]);
    setIndex(0);
  }, []);

  const nextCursor = query.data?.page.nextCursor ?? null;
  const total = query.data?.page.total ?? 0;
  const rowsOnPage = query.data?.data.length ?? 0;

  const paging = useMemo<SubmissionsPaging>(
    () => ({
      pageSize,
      setPageSize,
      pageNumber: index + 1,
      firstRow: total === 0 ? 0 : index * pageSize + 1,
      lastRow: index * pageSize + rowsOnPage,
      total,
      hasPrevious: index > 0,
      hasNext: Boolean(nextCursor),
      goNext: () => {
        if (!nextCursor) return;
        setCursors((previous) => {
          const next = previous.slice(0, index + 1);
          next.push(nextCursor);
          return next;
        });
        setIndex((previous) => previous + 1);
      },
      goPrevious: () => setIndex((previous) => Math.max(0, previous - 1)),
    }),
    [pageSize, setPageSize, index, total, rowsOnPage, nextCursor],
  );

  return { ...query, paging };
};

/**
 * The column list comes from the server, never from a hardcoded list in the UI — the same rule
 * that governs field types (CLAUDE.md). It also carries which columns are masked for this user,
 * so the table can say so rather than silently showing asterisks.
 */
export const useColumns = (formId: string) =>
  useQuery({
    queryKey: ["submission-columns", formId],
    queryFn: () => apiGet<{ data: ColumnDefinition[] }>(`/forms/${formId}/submissions/columns`),
    staleTime: 5 * 60_000,
  });

export const useSavedViews = (formId: string) =>
  useQuery({
    queryKey: ["saved-views", formId],
    queryFn: () => apiGet<{ data: SavedView[] }>(`/forms/${formId}/views`),
  });

export const useSaveView = (formId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<SavedView, "id" | "isMine" | "ownerName">) =>
      apiPost<{ id: string }>(`/forms/${formId}/views`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ["saved-views", formId] }),
  });
};

export const useDeleteView = (formId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) => apiDelete<void>(`/views/${viewId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ["saved-views", formId] }),
  });
};

export const useUpdateView = (formId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ viewId, ...body }: { viewId: string } & Partial<SavedView>) =>
      apiPatch<{ id: string }>(`/views/${viewId}`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ["saved-views", formId] }),
  });
};

/**
 * No `formId`: the drawer this hook serves is also opened from global search, where the submission
 * may belong to a form the user is not currently looking at. Invalidating the whole `submissions`
 * key is the honest scope — one deletion is rare, and a stale table is worse than a refetch.
 */
export const useDeleteSubmission = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (submissionId: string) => apiDelete<void>(`/submissions/${submissionId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["submissions"] });
      // The count drives the FULL state on the public form, so a stale one keeps an open form
      // looking closed.
      void client.invalidateQueries({ queryKey: ["forms"] });
    },
  });
};

export interface SubmissionDetail {
  id: string;
  form: { id: string; title: string; programmeId: string };
  formVersion: { version: number; publishedAt: string | null } | null;
  cycle: string | null;
  reference: string | null;
  status: string;
  reviewStatus: string;
  submittedAt: string | null;
  source: string;
  duplicateFlag: boolean;
  respondent: { id: string; displayName: string | null; email: string | null } | null;
  fields: {
    key: string;
    label: string;
    type: string;
    isPii: boolean;
    isSensitive: boolean;
    canReveal: boolean;
  }[];
  data: Record<string, unknown>;
  changedFields: unknown;
  consents: { policyKey: string; policyVersion: string; acceptedAt: string }[];
  auditTrail: { action: string; actorLabel: string | null; createdAt: string }[];
  piiMasked: boolean;
}

export const useSubmissionDetail = (submissionId: string | null) =>
  useQuery({
    queryKey: ["submission", submissionId],
    queryFn: () => apiGet<SubmissionDetail>(`/submissions/${submissionId ?? ""}`),
    enabled: submissionId !== null,
  });
